import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, HeartHandshake, BookOpen, User } from 'lucide-react';
import FloatingElements from './FloatingElements';

const OnboardingScreen = ({ onComplete, onSignIn, onNavigate }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden garden-bg"
    >
      {/* Floating butterflies and water animation */}
      <FloatingElements />

      {onSignIn && (
        <button
          type="button"
          onClick={onSignIn}
          className="absolute top-4 right-4 z-20 min-h-[44px] inline-flex items-center gap-2 px-4 rounded-full glass-card text-sm font-medium text-primary hover:underline"
          data-testid="homepage-signin-link"
        >
          <User className="w-4 h-4" aria-hidden="true" />
          Sign In
        </button>
      )}

      {/* Content - no full overlay, just cards with glass effect */}
      <div className="relative z-10 max-w-2xl text-center">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-arch-xl p-8 mb-8"
        >
          <h1 className="font-heading text-5xl sm:text-6xl font-semibold text-primary mb-4 tracking-tight">
            Sirati
          </h1>
          <p className="text-xl sm:text-2xl text-text-main mb-2 font-light">
            Your journey to marriage readiness starts here.
          </p>
          <p className="text-text-muted text-sm">
            A faith-centered community for Muslim singles preparing for marriage with intention.
          </p>
          {/* Mobile only: lets people join without scrolling past the cards */}
          <button
            onClick={onComplete}
            className="sm:hidden mt-6 inline-flex items-center justify-center gap-3 px-8 min-h-[48px] bg-primary text-primary-foreground rounded-full font-medium btn-hover shadow-lg"
            data-testid="onboarding-continue-btn-top"
          >
            Create My Free Account
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </button>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
        >
          <div className="glass-card rounded-arch-lg p-5 card-hover">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Compass className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-heading font-medium text-text-main mb-1">Know Yourself</h3>
            <p className="text-sm text-text-muted">Gain clarity around who you are and what you're looking for.</p>
          </div>

          <div className="glass-card rounded-arch-lg p-5 card-hover">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-6 h-6 text-secondary" />
            </div>
            <h3 className="font-heading font-medium text-text-main mb-1">Prepare With Intention</h3>
            <p className="text-sm text-text-muted">Grow in faith, character and emotional readiness.</p>
          </div>

          <div className="glass-card rounded-arch-lg p-5 card-hover">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <HeartHandshake className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-heading font-medium text-text-main mb-1">Stop Navigating Alone</h3>
            <p className="text-sm text-text-muted">Connect with a community of Muslim singles on a similar journey.</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <button
            onClick={onComplete}
            className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground rounded-full font-medium btn-hover text-lg shadow-lg"
            data-testid="onboarding-continue-btn"
          >
            Create My Free Account
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>

        {onNavigate && (
          <nav aria-label="About and policies" className="mt-8 text-sm text-text-main" data-testid="landing-footer">
            <button type="button" onClick={() => onNavigate('about')} className="min-h-[44px] px-2 hover:underline">About &amp; pricing</button>
            <span aria-hidden="true">&middot;</span>
            <button type="button" onClick={() => onNavigate('terms')} className="min-h-[44px] px-2 hover:underline">Terms</button>
            <span aria-hidden="true">&middot;</span>
            <button type="button" onClick={() => onNavigate('privacy')} className="min-h-[44px] px-2 hover:underline">Privacy</button>
            <span aria-hidden="true">&middot;</span>
            <a href="mailto:salam@sirati.co" className="inline-block min-h-[44px] leading-[44px] px-2 hover:underline">Contact</a>
          </nav>
        )}
      </div>
    </motion.div>
  );
};

export default OnboardingScreen;
